import request from 'lib/request';
import type { Response } from 'lib/request/types';
import {
  VolumeMetric,
  TypeCurveOptionsResponse,
  BulkTypeCurveVolumesResponse,
  TypeCurveVolumeRecord,
  TypeCurveRatioRecord,
  WellPDPDeclineMetricData,
} from 'types/api-responses';
import {
  ForecastItem,
  Production,
  TypeCurveVolumes,
  VisibleWellSpec,
  type WellsIndexingState,
} from './types';
import type { DcaWorkspaceResponse } from 'types/dca-workspaces';
import type {
  DcaWorkspaceWellApi,
  DcaWorkspaceWellRegressionTaskApi,
} from 'types/dca-workspace-wells-api/responses';
import { EMPTY_PRODUCTION } from './hooks';
import type { WellSelectionEntry, WellSelectionState } from './atoms';

export const errorMessageFromResponse = (data: unknown): string => {
  if (data && typeof data === 'object' && 'errors' in data) {
    const errors = (data as { errors?: Array<{ message?: string }> }).errors;
    const first = errors?.[0]?.message;
    if (first) return first;
  }
  if (data && typeof data === 'object' && 'message' in data) {
    const m = (data as { message?: string }).message;
    if (m) return m;
  }
  return 'Request failed';
};

/**
 * Picks the forecast job a well should select by default.
 *
 * The default is the newest non-failed forecast created in the workspace, so a
 * forecast appears in the DCA charts as soon as a Fit run or a manual save
 * produces one. Workspaces holding no forecasts of their own fall back to the
 * latest forecast promoted for the well in any workspace.
 *
 * @param {DcaWorkspaceWellRegressionTaskApi[]} jobs - The well's jobs, filtered
 * to those carrying a forecast.
 * @return {DcaWorkspaceWellRegressionTaskApi | undefined}
 */
export const pickDefaultForecastJob = (
  jobs: DcaWorkspaceWellRegressionTaskApi[]
): DcaWorkspaceWellRegressionTaskApi | undefined =>
  jobs.find((j) => j.latest) ??
  jobs.find((j) => j.globalPromoted) ??
  jobs.at(-1);

/**
 * Updates the stored forecast of each well in the well selection to match the
 * wells just loaded for the selected DCA scenario.
 *
 * A well gets its default forecast when it has none stored, when the stored
 * forecast is no longer one of its jobs, when it was just saved in the DCA
 * modal, or after a Fit run. A well with no forecast in the scenario has its
 * stored forecast cleared, since that forecast belongs to another scenario.
 *
 * @param {WellSelectionState} prev - The current well selection.
 * @param {Pick<DcaWorkspaceWellApi, 'uwi' | 'jobs'>[]} wells - The wells loaded
 * for the selected scenario.
 * @param {SyncWellSelectionOptions} options - What triggered the load.
 * @return {WellSelectionState} The updated selection, or `prev` when nothing
 * changed.
 */
export const syncWellSelectionForecasts = (
  prev: WellSelectionState,
  wells: Pick<DcaWorkspaceWellApi, 'uwi' | 'jobs'>[],
  options: SyncWellSelectionOptions
): WellSelectionState => {
  const items = { ...prev.items };
  const defaultSelected = prev.mode === 'all_selected';
  let changed = false;

  for (const well of wells) {
    const existing = items[well.uwi];
    const next = syncWellSelectionEntry(
      existing,
      well,
      defaultSelected,
      options
    );
    if (next === existing) continue;

    if (next) {
      items[well.uwi] = next;
    } else {
      delete items[well.uwi];
    }
    changed = true;
  }

  return changed ? { ...prev, items } : prev;
};

/** What triggered a load of the workspace wells. */
type SyncWellSelectionOptions = {
  /** The well just saved in the DCA modal. */
  savedUwi: string | null;
  /** Whether a Fit run just finished. */
  isPostRegression: boolean;
};

/**
 * Works out one well's selection entry for the wells just loaded for the
 * selected DCA scenario. See `syncWellSelectionForecasts`.
 *
 * @param {WellSelectionEntry | undefined} existing - The well's current entry.
 * @param {Pick<DcaWorkspaceWellApi, 'uwi' | 'jobs'>} well - The loaded well.
 * @param {boolean} defaultSelected - Whether a well with no entry is selected.
 * @param {SyncWellSelectionOptions} options - What triggered the load.
 * @return {WellSelectionEntry | undefined} The new entry, `existing` when it
 * stays the same, or `undefined` when the well needs no entry.
 */
const syncWellSelectionEntry = (
  existing: WellSelectionEntry | undefined,
  well: Pick<DcaWorkspaceWellApi, 'uwi' | 'jobs'>,
  defaultSelected: boolean,
  options: SyncWellSelectionOptions
): WellSelectionEntry | undefined => {
  const jobsWithForecast = well.jobs.filter(
    (j) => j.wellDcaForecastId !== null
  );
  const defaultJob = pickDefaultForecastJob(jobsWithForecast);

  if (!defaultJob) {
    if (!existing || existing.selected === defaultSelected) return undefined;
    if (!existing.dcaForecastId) return existing;
    return { ...existing, dcaForecastId: '', isLatestForecast: true };
  }

  const defaultForecastId = defaultJob.wellDcaForecastId!.toString();
  const defaultEntry = {
    selected: existing?.selected ?? defaultSelected,
    dcaForecastId: defaultForecastId,
    isLatestForecast: defaultJob.latest ?? true,
  };
  if (!existing) return defaultEntry;

  const storedId = existing.dcaForecastId;
  // Selection is kept per workspace in localStorage, so a stored id
  // outlives the forecast it names: a later run replaces the forecasts
  // and the old ones are deleted. An id the well no longer has a job for
  // points at nothing, and the tearsheet would ask the forecast service
  // about it and be told there is nothing there.
  const isStoredIdStale =
    !!storedId &&
    !jobsWithForecast.some(
      (job) => job.wellDcaForecastId!.toString() === storedId
    );
  const useDefault =
    !storedId ||
    well.uwi === options.savedUwi ||
    isStoredIdStale ||
    (options.isPostRegression && storedId !== defaultForecastId);

  return useDefault ? defaultEntry : existing;
};

// ── Bulk fetch helpers ──

export const fetchBulkProduction = async (
  uwis: string[],
  ac: AbortController,
  dcaScenarioId?: string | null
): Promise<Record<string, Production>> => {
  const res = await request.post<VolumeMetric, unknown>(
    '/queries/reservoir/eval_volumes',
    {
      query: {
        // Each well's production comes from the first source in the
        // scenario's hierarchy that it has data for.
        ...(dcaScenarioId ? { dcaScenarioId } : {}),
        productionVolumes: [{ uwis, versionIds: ['current'] }],
        responseConfig: {
          includeRatiosProduction: true,
          includeMbtProduction: true,
          includeCumulativeProduction: true,
        },
      },
    },
    ac
  );

  const result: Record<string, Production> = {};
  if (res.type === 'success') {
    for (const uwi of uwis) {
      const m = res.data?.pdpDeclines?.[uwi]?.[0];
      result[uwi] = m
        ? {
            volume: m.volumes ?? [],
            cumulative: m.cumulatives ?? [],
            ratio: m.ratios ?? [],
            materialBalanceTime: m.mbts ?? [],
            source: m.productionSource,
          }
        : EMPTY_PRODUCTION;
    }
  }
  return result;
};

export const fetchBulkForecasts = async (
  specs: VisibleWellSpec[],
  ac: AbortController
): Promise<Record<string, ForecastItem[]>> => {
  const forecastIds = [
    ...new Set(specs.map((s) => s.dcaForecastId).filter(Boolean)),
  ];
  if (forecastIds.length === 0) return {};

  const forecastNotVolumesPromise = request.post<
    { pdpDeclines?: Record<string, WellPDPDeclineMetricData[]> },
    unknown
  >(
    '/queries/reservoir/eval_volumes',
    {
      query: {
        productionVolumes: [
          {
            uwis: specs.map((s) => s.uwi),
            versionIds: ['current'],
          },
        ],
        pdpForecast: [
          {
            wellDcaForecastIds: forecastIds,
          },
        ],
        responseConfig: {
          includeRatiosForecast: true,
          includeCumulativeForecast: true,
          includeMbtForecast: true,
          useForecastStartAsTransition: true,
        },
      },
    },
    ac
  );

  const forecastVolumesPromise = request.post<
    { pdpDeclines?: Record<string, WellPDPDeclineMetricData[]> },
    unknown
  >(
    '/queries/reservoir/eval_volumes',
    {
      query: {
        productionVolumes: [
          {
            uwis: specs.map((s) => s.uwi),
            versionIds: ['current'],
          },
        ],
        pdpForecast: [
          {
            wellDcaForecastIds: forecastIds,
          },
        ],
        responseConfig: {
          includeOnlyDcaForecast: true,
        },
      },
    },
    ac
  );

  // Use Promise.all (and not Promise.allSettled) to be able to extract the
  // successful `resolve`s of each promise.
  //
  // NOTE: `request.post` does not actually reject; it always returns
  // `Response`, with its `type` field indicates request success or failure.
  const [forecastNotVolumesRes, forecastVolumesRes] = await Promise.all([
    forecastNotVolumesPromise,
    forecastVolumesPromise,
  ]);

  const result: Record<string, ForecastItem[]> = {};

  // Extracts the PDP Declines from the response of `/queries/eval_volumes`,
  // logging errors to the console.
  const pdpDeclinesFrom = (
    res: Response<{ pdpDeclines?: Record<string, WellPDPDeclineMetricData[]> }>
  ): Record<string, WellPDPDeclineMetricData[]> | undefined => {
    if (res.type === 'success') return res.data?.pdpDeclines;
    if (res.type === 'error' || res.type === 'fatal') {
      console.error('eval_volumes forecast fetch failed:', res);
    }
    return undefined;
  };

  const notVolumesByUwi = pdpDeclinesFrom(forecastNotVolumesRes);
  const volumesByUwi = pdpDeclinesFrom(forecastVolumesRes);

  for (const spec of specs) {
    const forecastNotVolumesData = notVolumesByUwi?.[spec.uwi];
    const forecastVolumesData = volumesByUwi?.[spec.uwi];
    const forecasts: ForecastItem[] = [];
    if (forecastNotVolumesData) {
      for (let i = 0; i < forecastNotVolumesData.length; i++) {
        const item = forecastNotVolumesData[i];
        const volumes = forecastVolumesData?.[i]?.volumes ?? [];
        if (volumes.length === 0) continue;
        forecasts.push({
          id: `forecast-${
            item.wellDcaForecastId ?? item.pdpForecastVersionId ?? 'unknown'
          }`,
          name: 'Forecast',
          volume: volumes,
          cumulative: item.cumulatives ?? [],
          ratio: item.ratios ?? [],
          materialBalanceTime: item.mbts ?? [],
          isDraggable: false,
        });
      }
    }
    result[spec.uwi] = forecasts;
  }

  return result;
};

export const fetchBulkTypeCurves = async (
  uwis: string[],
  dcaScenarioId: string,
  ac: AbortController
): Promise<Record<string, TypeCurveVolumes | null>> => {
  const headersRes = await request.post<TypeCurveOptionsResponse, unknown>(
    '/queries/wells/type_curve_headers',
    { bulkTypeCurveHeaders: uwis.map((uwi) => ({ uwi, active: true })) },
    ac
  );

  if (ac.signal.aborted) return {};

  const result: Record<string, TypeCurveVolumes | null> = {};
  for (const uwi of uwis) result[uwi] = null;

  if (headersRes.type !== 'success') return result;

  const bulkReqs: Array<{
    uwi: string;
    typeCurveHeaderDetailsId: string;
    dcaScenarioId: number;
    perforatedLateralLengthFt: number;
  }> = [];
  const tcIdByUwi: Record<string, string> = {};

  for (const uwi of uwis) {
    const headers = headersRes.data?.results?.[uwi];
    const sel =
      headers && headers.length > 0
        ? headers.find((h) => h.isPreselected)
        : null;
    if (sel) {
      tcIdByUwi[uwi] = sel.uniqueTcId;
      bulkReqs.push({
        uwi,
        typeCurveHeaderDetailsId: sel.id,
        dcaScenarioId: Number(dcaScenarioId),
        perforatedLateralLengthFt: 10000,
      });
    }
  }

  if (bulkReqs.length === 0) return result;

  const tcRes = await request.post<BulkTypeCurveVolumesResponse, unknown>(
    '/queries/reservoir/bulk_type_curves',
    {
      bulkTypeCurves: bulkReqs,
      responseConfig: {
        scaling: true,
        include_volumes: true,
        include_cumulatives: true,
        include_ratios: true,
        include_mbts: true,
      },
    },
    ac
  );

  if (ac.signal.aborted) return {};

  if (tcRes.type === 'success') {
    for (const uwi of uwis) {
      const tcId = tcIdByUwi[uwi];
      if (!tcId) continue;
      const d = tcRes.data?.results?.find((r) => r.uniqueTcId === tcId);
      if (d) {
        result[uwi] = {
          volume: (d.volumes ?? []) as TypeCurveVolumeRecord[],
          cumulative: (d.cumulatives ?? []) as TypeCurveVolumeRecord[],
          ratio: (d.ratios ?? []) as TypeCurveRatioRecord[],
          materialBalanceTime: (d.mbts ?? []) as TypeCurveVolumeRecord[],
        };
      }
    }
  }

  return result;
};

/**
 * Reads how far along indexing the workspace's wells into Elasticsearch is.
 *
 * @param {DcaWorkspaceResponse} workspace - The workspace as the API returned it.
 * @return {WellsIndexingState} `done` once the wells are indexed.
 */
export const wellsIndexingState = (
  workspace: Pick<
    DcaWorkspaceResponse,
    | 'hasWellsIndexingInProgress'
    | 'hasWellsIndexingSlow'
    | 'hasWellsIndexingFailed'
  >
): WellsIndexingState => {
  if (!workspace.hasWellsIndexingInProgress) return 'done';
  if (workspace.hasWellsIndexingFailed) return 'failed';
  if (workspace.hasWellsIndexingSlow) return 'slow';
  return 'in_progress';
};

/** The banner shown for each indexing state other than `done`. */
export const WELLS_INDEXING_BANNERS: Record<
  Exclude<WellsIndexingState, 'done'>,
  { title: string; message: string; kind: 'info' | 'warning' | 'error' }
> = {
  in_progress: {
    title: 'Indexing in progress',
    message:
      'Wells are being indexed for this workspace. Data will load automatically once indexing is complete.',
    kind: 'info',
  },
  slow: {
    title: 'Indexing is taking longer than usual',
    message:
      'Wells usually finish indexing within a few minutes. Data will load automatically once indexing is complete.',
    kind: 'warning',
  },
  failed: {
    title: 'Indexing failed',
    message:
      'The wells for this workspace could not be indexed, so the well table, map and plots have nothing to show. Create the workspace again, or ask in #title-chaining-tool on Slack if it keeps happening.',
    kind: 'error',
  },
};
